import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  applyDatasetPriceUpdate,
  applyPriceUpdate,
  getDatasetPriceUpdateSummary,
  getPriceUpdateSummary,
  type DatasetPriceUpdateRequest,
  type DatasetPriceUpdateResponse,
  type DatasetPriceUpdateSummary,
  type PriceUpdateCurrency,
  type PriceUpdateDataset,
  type PriceUpdateRequest,
  type PriceUpdateResponse,
  type PriceUpdateSummary,
} from "../api/priceUpdate";

/**
 * TanStack Query key of the dataset list a bulk change invalidates, so the
 * table repaints with the new prices right after the modal closes.
 */
const DATASET_LIST_KEY: Record<PriceUpdateDataset, string> = {
  "pemserter-price-list": "pemserter-price-list-eur",
  "profil-indie-options": "profile-indie-options",
  "sherex-options": "sherex-options",
  "autoriv-configurator-items": "autoriv-configurator-items",
};

export function usePriceUpdateSummary(enabled = true) {
  return useQuery<PriceUpdateSummary>({
    queryKey: ["price-update", "summary"],
    queryFn: () => getPriceUpdateSummary(),
    enabled,
    staleTime: 30_000,
  });
}

export function useApplyPriceUpdate() {
  const queryClient = useQueryClient();
  return useMutation<
    PriceUpdateResponse,
    Error,
    { currency: PriceUpdateCurrency; body: PriceUpdateRequest }
  >({
    mutationFn: ({ currency, body }) => applyPriceUpdate(currency, body),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["price-update"] });
      // Invalidate the affected dataset so the table reflects the new prices.
      const key =
        vars.currency === "EUR"
          ? ["pemserter-price-list-eur"]
          : ["pemserter-price-list-usd"];
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/** In-scope price count + last bulk change for one dataset/region. */
export function useDatasetPriceUpdateSummary(
  dataset: PriceUpdateDataset,
  regionId: number | undefined,
  enabled = true,
) {
  return useQuery<DatasetPriceUpdateSummary>({
    queryKey: ["price-update", "dataset", dataset, regionId ?? null],
    queryFn: () => getDatasetPriceUpdateSummary(dataset, regionId),
    enabled,
    staleTime: 30_000,
  });
}

export function useApplyDatasetPriceUpdate(dataset: PriceUpdateDataset) {
  const queryClient = useQueryClient();
  return useMutation<DatasetPriceUpdateResponse, Error, DatasetPriceUpdateRequest>({
    mutationFn: (body) => applyDatasetPriceUpdate(dataset, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["price-update"] });
      void queryClient.invalidateQueries({ queryKey: [DATASET_LIST_KEY[dataset]] });
    },
  });
}
